import { StorageError } from '../../app/errors';
import type { PlayerRow, TeamRow } from '../../types';
import { requireTransaction } from '../commit';
import { db, TABLE } from '../db';

/**
 * `teams` and `players` — the profile scope of a per-match archive (SYN-4) and the outer scope of
 * INV-8's cascade.
 *
 * **`upsertPlayer` is why `&[teamId+number]` exists.** PRE-4 resolves a shirt-number slot by
 * upserting a `Player` on `[teamId + number]`, creating the row on demand and binding
 * `Hypothesis.playerId` to it (FILE-2). Design §3.4: *"Without it the upsert silently degrades to an
 * insert and FILE-2's cross-match player linkage duplicates a shirt number per match."* The index
 * makes the lookup exact; this function is the only path that uses it, so the degradation has
 * nowhere to happen.
 */

/** @throws {StorageError} outside a transaction, on a duplicate id, or on a write failure. */
export async function insertTeam(row: TeamRow): Promise<void> {
  requireTransaction('insertTeam');
  try {
    await db().teams.add(row);
  } catch (cause) {
    throw new StorageError('Could not create the team.', {
      cause,
      context: { store: TABLE.teams, id: row.id },
    });
  }
}

/** PRE-1 / FILE-2 — team details are editable after creation. @throws {StorageError} */
export async function updateTeam(
  id: string,
  changes: Partial<Pick<TeamRow, 'name' | 'club' | 'ageGroup' | 'notes'>>,
): Promise<void> {
  requireTransaction('updateTeam');
  const row = await db().teams.get(id);
  if (!row) {
    throw new StorageError(`Team ${id} no longer exists; nothing was written.`, {
      context: { store: TABLE.teams, id },
    });
  }
  try {
    await db().teams.put({ ...row, ...changes });
  } catch (cause) {
    throw new StorageError('Could not update the team.', {
      cause,
      context: { store: TABLE.teams, id },
    });
  }
}

/**
 * Writes one `Player` under a **caller-supplied id**. Two callers, one reason.
 *
 * **BAK-2 / SYN-5 import.** `upsertPlayer` cannot serve it: it resolves by `[teamId+number]` and
 * mints an id for a row it creates, so importing through it would rewrite every
 * `Hypothesis.playerId` reference in the archive to point at a player id that never existed on the
 * source device (FILE-2's cross-match linkage, silently broken on restore). SYN-5 is explicit that
 * Team and Player rows "deduplicate by id"; this is the writer that lets them.
 *
 * **PRE-4's `CreatePlayer` command** (build unit U-7), for the same shape of reason one layer up: the
 * `HypothesisRow` is pre-built before the transaction opens and carries `playerId`, so the id has to
 * be decided by the caller. The handler reads `[teamId+number]` inside the transaction and refuses
 * when a *different* row already holds it, which is the check `upsertPlayer` would have folded into
 * a silent rebind.
 *
 * `upsertPlayer` cannot serve BAK-2: it resolves by `[teamId+number]` and mints an id for a row it
 * creates, so importing through it would rewrite every `Hypothesis.playerId` reference in the
 * archive to point at a player id that never existed on the source device (FILE-2's cross-match
 * linkage, silently broken on restore). SYN-5 is explicit that Team and Player rows "deduplicate by
 * id"; this is the writer that lets them.
 *
 * `add`, not `put`: BAK-2 writes into a profile the same transaction has just emptied, so a
 * collision means the wipe did not happen. The unique `&[teamId+number]` still applies — two
 * archived players sharing a shirt number on one team abort the import, which is the right outcome
 * for a file that could not have been produced by this app.
 *
 * @throws {StorageError} outside a transaction, on a duplicate id or shirt number, or on a write
 *   failure.
 */
export async function insertPlayer(row: PlayerRow): Promise<void> {
  requireTransaction('insertPlayer');
  try {
    await db().players.add(row);
  } catch (cause) {
    throw new StorageError('Could not import the player.', {
      cause,
      context: { store: TABLE.players, id: row.id, teamId: row.teamId, number: row.number },
    });
  }
}

/**
 * PRE-4 — resolves a shirt number to a `Player`, creating the row on demand.
 *
 * Idempotent by `[teamId+number]`: re-running the same slot resolution in a later match binds to the
 * **same** player row rather than making a second one, which is what makes FILE-2's cross-match
 * linkage work at all.
 *
 * @param mintId supplies the id for the row this may create. Passed in rather than generated here,
 *   because id minting is not the storage layer's decision and because a repo that invented ids
 *   would be non-deterministic in a fixture.
 * @returns the player row that now backs `[teamId+number]`.
 * @throws {StorageError} outside a transaction, on an out-of-range number, or on a write failure.
 */
export async function upsertPlayer(
  input: { teamId: string; number: number; foot?: PlayerRow['foot']; notes?: string | null },
  mintId: () => string,
): Promise<PlayerRow> {
  requireTransaction('upsertPlayer');
  // PRE-4's number wheel is 1–99; a value outside it can only come from an import.
  if (!Number.isInteger(input.number) || input.number < 1 || input.number > 99) {
    throw new StorageError(
      `PRE-4: a shirt number must be an integer in [1, 99]; got ${String(input.number)}.`,
      { context: { store: TABLE.players, teamId: input.teamId, number: input.number } },
    );
  }
  const table = db().players;
  let existing: PlayerRow | undefined;
  try {
    existing = await table.where('[teamId+number]').equals([input.teamId, input.number]).first();
  } catch (cause) {
    throw new StorageError('Could not look up the player by shirt number.', {
      cause,
      context: { store: TABLE.players, teamId: input.teamId, number: input.number },
    });
  }

  const next: PlayerRow = existing
    ? {
        ...existing,
        foot: input.foot ?? existing.foot,
        notes: input.notes === undefined ? existing.notes : input.notes,
      }
    : {
        id: mintId(),
        teamId: input.teamId,
        number: input.number,
        foot: input.foot ?? null,
        notes: input.notes ?? null,
      };

  try {
    await table.put(next);
  } catch (cause) {
    throw new StorageError('Could not save the player.', {
      cause,
      context: { store: TABLE.players, teamId: input.teamId, number: input.number },
    });
  }
  return next;
}

/** INV-8's deletion path. Named for its single caller. @throws {StorageError} */
export async function deleteTeamsForCascade(ids: readonly string[]): Promise<void> {
  requireTransaction('deleteTeamsForCascade');
  if (ids.length === 0) return;
  try {
    await db().teams.bulkDelete([...ids]);
  } catch (cause) {
    throw new StorageError('Could not delete the teams during a cascade.', {
      cause,
      context: { store: TABLE.teams, count: ids.length },
    });
  }
}

/** INV-8's deletion path. Named for its single caller. @throws {StorageError} */
export async function deletePlayersForCascade(ids: readonly string[]): Promise<void> {
  requireTransaction('deletePlayersForCascade');
  if (ids.length === 0) return;
  try {
    await db().players.bulkDelete([...ids]);
  } catch (cause) {
    throw new StorageError('Could not delete the players during a cascade.', {
      cause,
      context: { store: TABLE.players, count: ids.length },
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Reads
// ─────────────────────────────────────────────────────────────────────────────

/** @throws {StorageError} on a read failure. */
export async function getTeam(id: string): Promise<TeamRow | undefined> {
  try {
    return await db().teams.get(id);
  } catch (cause) {
    throw new StorageError('Could not read the team.', {
      cause,
      context: { store: TABLE.teams, id },
    });
  }
}

/** Home's opponent list, by name. @throws {StorageError} */
export async function listTeams(): Promise<TeamRow[]> {
  try {
    return await db().teams.orderBy('name').toArray();
  } catch (cause) {
    throw new StorageError('Could not read the team list.', { cause });
  }
}

/**
 * PRE-4 / FILE-2 — the row already holding `[teamId+number]`, if any.
 *
 * The read half of the upsert `upsertPlayer` performs in one step, split out because the
 * `CreatePlayer` command needs the answer *as a snapshot* — read inside the transaction, handed to a
 * pure handler, which is design §1.1's shape. Uses the same unique compound index, so the two paths
 * cannot disagree about what "already exists" means.
 *
 * @throws {StorageError} on a read failure.
 */
export async function findPlayerByNumber(
  teamId: string,
  number: number,
): Promise<PlayerRow | undefined> {
  try {
    return await db().players.where('[teamId+number]').equals([teamId, number]).first();
  } catch (cause) {
    throw new StorageError('Could not look up the player by shirt number.', {
      cause,
      context: { store: TABLE.players, teamId, number },
    });
  }
}

/** @throws {StorageError} on a read failure. */
export async function listPlayersForTeam(teamId: string): Promise<PlayerRow[]> {
  try {
    return await db().players.where('teamId').equals(teamId).toArray();
  } catch (cause) {
    throw new StorageError('Could not read the team’s players.', {
      cause,
      context: { store: TABLE.players, teamId },
    });
  }
}
